import React, { useEffect, useState } from 'react';
import PetPhoto from './PetPhoto';
import type { Pet } from '../types';
import '../styles/PetGallery.css';

interface PetGalleryProps {
  pet: Pet;
  /** Maximum thumbnails to render under the main image. */
  maxThumbnails?: number;
}

const THUMB_W = 200;
const THUMB_H = 200;

function thumbSrc(url: string): string {
  if (!url.includes('images.unsplash.com')) return url;
  try {
    const u = new URL(url);
    u.searchParams.set('w', String(THUMB_W));
    u.searchParams.set('h', String(THUMB_H));
    u.searchParams.set('fit', 'crop');
    u.searchParams.set('auto', 'format');
    u.searchParams.set('q', '70');
    return u.toString();
  } catch {
    return url;
  }
}

const PetGallery: React.FC<PetGalleryProps> = ({ pet, maxThumbnails = 6 }) => {
  const photos = pet.photoUrls.slice(0, maxThumbnails);
  const [active, setActive] = useState(0);

  // Reset active when the pet changes (e.g. user navigates between detail pages).
  useEffect(() => {
    setActive(0);
  }, [pet.id]);

  if (photos.length === 0) {
    return (
      <div className="pet-gallery pet-gallery--single">
        <div className="pet-gallery__main pet-gallery__main--silhouette">
          <PetPhoto pet={pet} loading="eager" variant="hero" />
        </div>
      </div>
    );
  }

  const safeActive = Math.min(active, photos.length - 1);

  return (
    <div className="pet-gallery">
      <div className="pet-gallery__main">
        <PetPhoto
          pet={pet}
          photoUrl={photos[safeActive]}
          loading="eager"
          variant="hero"
        />
      </div>

      {photos.length > 1 && (
        <ul className="pet-gallery__strip" role="tablist" aria-label="Mais fotos">
          {photos.map((url, i) => (
            <li key={url}>
              <button
                type="button"
                role="tab"
                aria-selected={i === safeActive}
                aria-label={`Foto ${i + 1} de ${photos.length}`}
                className={`pet-gallery__thumb ${i === safeActive ? 'is-active' : ''}`}
                onClick={() => setActive(i)}
              >
                <img
                  src={thumbSrc(url)}
                  width={THUMB_W}
                  height={THUMB_H}
                  alt=""
                  loading="lazy"
                  decoding="async"
                />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PetGallery;
